import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCarePlanInstance,
  // draftCarePlanDetailView,
} from "../../../redux/actions/careplanActions";
import { clearCarePlan } from "../../../redux/slices/careplanSlice";
import styled from "styled-components";
import { mapColor } from "../../../utils/colorUtils";
import FullScreenOverlay from "../../common/FullScreenOverlay";
import FloatingMenu from "../../common/FloatingMenu";
import UrgentCare from "./UrgentCare";
import Spacer from "../../common/Spacer";
import { PublishIcon, LinedRightArrow } from "../../common/Icons";
import Avatar from "../../common/Avatar";
import ItemThumbnail from "../../common/ItemThumbnail";

import {
  OuterWrapper,
  HeaderWrapper,
  HeaderContainer,
  TextOverlay,
  TextContainer,
  BottomTextContainer,
  BottomTextColumn,
  BodyContainer,
  SectionContainer,
} from "../../common/PrintHelper";

const DraftPlanView = ({ carePlanId, onClose }) => {
  const dispatch = useDispatch();
  const plan = useSelector((state) => state.careplan.carePlan);
  const {
    sections = [],
    contacts = [],
    nhsNumber,
    important_info,
    mdtUsers = [],
    public_id,
  } = plan;

  // useGoogleTranslate();

  useEffect(() => {
    dispatch(clearCarePlan());
    dispatch(fetchCarePlanInstance(carePlanId));
    // dispatch(draftCarePlanDetailView(carePlanId));
  }, [carePlanId, dispatch]);

  if (!nhsNumber) {
    return <div>Loading...</div>;
  }

  const handlePrint = () => {
    if (!public_id) {
      console.warn("no public_id on", plan);
      return;
    }

    sessionStorage.setItem(`draftPlan-${public_id}`, JSON.stringify(plan));

    const url = `${window.location.origin}/draft-print/${public_id}`;
    window.open(url, "_blank");
  };

  // const handleClose = () => {
  //   // 1. clear the translate cookie so English is the default
  //   document.cookie = [
  //     "googtrans=/",
  //     `domain=${window.location.hostname}`,
  //     "path=/",
  //     "expires=Thu, 01 Jan 1970 00:00:00 GMT",
  //   ].join("; ");

  //   // 2. close the overlay
  //   onClose();

  //   // 3. reload the page to undo any translation
  //   window.location.reload();
  // };

  const menuItems = [
    {
      label: "Print plan",
      icon: <PublishIcon color="#004F96" />,
      action: handlePrint,
    },
  ];

  return (
    <>
      <FullScreenOverlay
        title={`Draft Care Plan: ${nhsNumber}`}
        onClose={onClose}
        width="100%"
        height="100%"
        iconClose={true}
        zIndex={200}
      >
        {/* <div id="google_translate_element"></div> */}

        <OuterWrapper>
          <HeaderWrapper>
            <HeaderContainer>
              <TextOverlay>
                <TextContainer>
                  <img src="/assets/SidebarLogoImage.svg" alt="Sidebar Logo" />
                  <BottomTextContainer>
                    <BottomTextColumn>
                      <p className="body-text-small medium secondary-700">
                        NHS number
                      </p>
                      <p className="body-text-regular semi-bold neutral-700">
                        {nhsNumber}
                      </p>
                    </BottomTextColumn>
                  </BottomTextContainer>
                </TextContainer>
              </TextOverlay>
              <Spacer height="24px" />
            </HeaderContainer>
          </HeaderWrapper>

          <BodyContainer>
            {sections
              .filter((section) =>
                section.items.some((item) => item.answer?.trim())
              )
              .map((section) => (
                <SectionContainer key={section.id}>
                  <SectionTitle color={section.color}>
                    <h4 className="semi-bold neutral-100">{section.title}</h4>
                  </SectionTitle>
                  <Spacer height="16px" />

                  <DigitalOnly>
                    {section.resource_link && (
                      <FindOutMore
                        className="primary-600"
                        onClick={() =>
                          window.open(section.resource_link, "_blank")
                        }
                      >
                        Find out more <LinedRightArrow />
                      </FindOutMore>
                    )}
                  </DigitalOnly>
                  <Spacer height="32px" />

                  <SectionCard>
                    {section.items
                      .filter((item) => item.answer?.trim())
                      .map((item) => (
                        <ItemCardWrapper key={item.id}>
                          <ItemCard>
                            <ItemThumbnail
                              src={item?.image_url}
                              alt={item?.image_alt_text || item?.title}
                              width={"150px"}
                              height={"150px"}
                            />

                            <ItemContent>
                              <h6 className="semi-bold">{item.title}</h6>
                              <p>{item.answer}</p>

                              {item.resource_link && (
                                <DigitalOnly>
                                  <FindOutMore
                                    onClick={() =>
                                      window.open(
                                        item.resource_link,
                                        "_blank",
                                        "noopener,noreferrer"
                                      )
                                    }
                                  >
                                    View resources <LinedRightArrow />
                                  </FindOutMore>
                                </DigitalOnly>
                              )}
                            </ItemContent>
                          </ItemCard>
                        </ItemCardWrapper>
                      ))}
                  </SectionCard>
                  <Spacer height="16px" />
                </SectionContainer>
              ))}

            <ImportantInfoContainer>
              <UrgentCare
                retrievedContacts={contacts}
                mode="view"
                initialImportantInfo={important_info}
              />
            </ImportantInfoContainer>
          </BodyContainer>

          <FooterContainer>
            <ContributersWrapper>
              <Contributers>
                <PeopleContainerWrapper>
                  <h6 className="semi-bold">Care plan collaborators</h6>
                  <PeopleContainer>
                    {mdtUsers.length > 0 ? (
                      mdtUsers.map((user) => (
                        <UserCard key={user.id}>
                          <Avatar
                            profilePictureUrl={user.profile_picture}
                            name={user.name}
                          />

                          <UserDetails>
                            <p className="body-text-regular semi-bold neutral-900">
                              {user.name}
                            </p>
                            <p className="body-text-small regular neutral-900">
                              {user?.roles[0]?.name || "Team member"}
                            </p>
                          </UserDetails>
                        </UserCard>
                      ))
                    ) : (
                      <p>No collaborators listed</p>
                    )}
                  </PeopleContainer>
                </PeopleContainerWrapper>
              </Contributers>
            </ContributersWrapper>
          </FooterContainer>
        </OuterWrapper>

        <FloatingMenu items={menuItems} position="bottom-center" />
      </FullScreenOverlay>
    </>
  );
};

export default DraftPlanView;

// --- Styled Components ---

const SectionTitle = styled.div`
  padding: 16px;
  border-radius: 12px;
  background-color: ${(props) =>
    mapColor(props.color || "#3A70E2").backgroundColor};
  h4 {
    color: ${(props) => mapColor(props.color || "#3A70E2").fontColor};
  }
`;

const SectionCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ItemCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ItemCard = styled.div`
  display: flex;
  gap: 16px;
  padding: 16px;
  border: 1px solid #ced4da;
  border-radius: 12px;
  @media print {
    page-break-inside: avoid;
  }
`;

const ItemContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DigitalOnly = styled.div`
  @media print {
    display: none !important;
  }
`;

const FindOutMore = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

const ImportantInfoContainer = styled.div`
  padding: 16px;
  border: 1px solid #ec2d30;
  background: rgba(236, 45, 48, 0.05);
  border-radius: 12px;
  @media print {
    page-break-inside: avoid;
  }
`;

const FooterContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`;

const ContributersWrapper = styled.div`
  display: flex;
  padding: 0px 24px 24px 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  align-self: stretch;

  background: var(--Colors-Neutral-200, #f8f9fa);
`;

const Contributers = styled.div`
  display: flex;
  padding: 24px 0px;
  flex-direction: column;
  align-items: flex-start;
  gap: 32px;
  align-self: stretch;
`;

const PeopleContainerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  align-self: stretch;
`;

const PeopleContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-self: stretch;
`;

const UserCard = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: calc(33.33% - 16px); // 3 per row with gap
  min-width: 200px; // Minimum width for each card

  @media (max-width: 768px) {
    width: calc(50% - 12px); // 2 per row on smaller screens
  }

  @media (max-width: 480px) {
    width: 100%; // 1 per row on mobile
  }
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
`;
